<template>
  <div class="day-reports-root">
    <div class="day-reports-dock">
      <button type="button" class="day-reports-dock__btn" @click="open('x')">
        X report
      </button>
      <button type="button" class="day-reports-dock__btn" @click="open('z')">
        Z report
      </button>
    </div>

    <div
      v-if="visible"
      class="day-reports-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="kindTitle"
      @click.self="close"
    >
      <div class="day-reports">
        <div class="day-reports__head">
          <h2>{{ kindTitle }}</h2>
          <input
            v-model="date"
            class="day-reports__date"
            type="date"
            aria-label="Report date"
            @change="load"
          />
          <button type="button" class="day-reports__close" aria-label="Close report" @click="close">
            Close
          </button>
        </div>

        <p class="day-reports__note">{{ kindNote }}</p>

    <div v-if="loading" class="day-reports__state">Loading report…</div>
    <div v-else-if="error" class="day-reports__state day-reports__state--error">
      {{ error }}
      <button type="button" class="day-reports__retry" @click="load">Try again</button>
    </div>

    <div v-else-if="report" class="day-reports__body">
      <section class="day-reports__block">
        <h3>Sales</h3>
        <dl>
          <div><dt>Orders</dt><dd>{{ report.orders.count }}</dd></div>
          <div><dt>Open</dt><dd>{{ report.orders.open_count }}</dd></div>
          <div><dt>Paid</dt><dd>{{ report.orders.paid_count }}</dd></div>
          <div><dt>Partial</dt><dd>{{ report.orders.partial_count }}</dd></div>
          <div><dt>Cancelled</dt><dd>{{ report.orders.cancelled_count }}</dd></div>
          <div><dt>Gross</dt><dd>{{ money(report.orders.gross) }}</dd></div>
          <div><dt>Discount</dt><dd>{{ money(report.orders.discount) }}</dd></div>
          <div><dt>Service</dt><dd>{{ money(report.orders.service_charge) }}</dd></div>
          <div><dt>Tax</dt><dd>{{ money(report.orders.tax) }}</dd></div>
          <div class="is-strong"><dt>Net</dt><dd>{{ money(report.orders.net) }}</dd></div>
          <div><dt>Collected</dt><dd>{{ money(report.orders.paid) }}</dd></div>
          <div class="is-due"><dt>Due</dt><dd>{{ money(report.orders.due) }}</dd></div>
        </dl>
      </section>

      <section class="day-reports__block">
        <h3>By type</h3>
        <p v-if="!report.orders.by_type.length" class="day-reports__empty">No sales</p>
        <table v-else>
          <thead>
            <tr><th>Type</th><th>Qty</th><th>Net</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in report.orders.by_type" :key="row.name">
              <td>{{ row.name }}</td>
              <td>{{ row.count }}</td>
              <td>{{ money(row.net) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="day-reports__block">
        <h3>Payments</h3>
        <p v-if="!report.payments.rows.length" class="day-reports__empty">No payments</p>
        <table v-else>
          <thead>
            <tr><th>Type</th><th>Status</th><th>Amt</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in report.payments.rows" :key="row.status + row.type">
              <td>{{ row.type }}</td>
              <td>{{ row.status }}</td>
              <td>{{ money(row.amount) }}</td>
            </tr>
          </tbody>
        </table>
        <dl>
          <div class="is-strong">
            <dt>Completed</dt>
            <dd>{{ money(report.payments.total_completed) }}</dd>
          </div>
          <div>
            <dt>Pending</dt>
            <dd>{{ money(report.payments.total_pending) }}</dd>
          </div>
        </dl>
      </section>

      <section class="day-reports__block">
        <h3>Waiters</h3>
        <p v-if="!report.orders.by_waiter.length" class="day-reports__empty">No waiter sales</p>
        <table v-else>
          <thead>
            <tr><th>Waiter</th><th>Qty</th><th>Net</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in report.orders.by_waiter" :key="row.name">
              <td>{{ row.name }}</td>
              <td>{{ row.count }}</td>
              <td>{{ money(row.net) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="day-reports__block">
        <h3>Open orders</h3>
        <p v-if="!report.open_orders.length" class="day-reports__empty">None open</p>
        <table v-else>
          <thead>
            <tr><th>Order</th><th>Place</th><th>Due</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in report.open_orders" :key="row.id">
              <td>{{ row.order_number }}</td>
              <td>{{ row.place }}</td>
              <td>{{ money(row.due) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="day-reports__block">
        <h3>Bookings</h3>
        <dl>
          <div><dt>Count</dt><dd>{{ report.reservations.count }}</dd></div>
          <div><dt>Amount</dt><dd>{{ money(report.reservations.amount) }}</dd></div>
          <div><dt>Paid</dt><dd>{{ money(report.reservations.paid) }}</dd></div>
          <div class="is-due"><dt>Due</dt><dd>{{ money(report.reservations.due) }}</dd></div>
        </dl>
      </section>

      <section class="day-reports__block">
        <h3>Expenses</h3>
        <dl>
          <div><dt>Count</dt><dd>{{ report.expenses.count }}</dd></div>
          <div><dt>Amount</dt><dd>{{ money(report.expenses.amount) }}</dd></div>
        </dl>
      </section>

      <section class="day-reports__block">
        <h3>Cancelled</h3>
        <dl>
          <div><dt>Orders</dt><dd>{{ report.orders.cancelled_count }}</dd></div>
          <div><dt>Value</dt><dd>{{ money(report.orders.cancelled_net) }}</dd></div>
        </dl>
      </section>
    </div>

        <div class="day-reports__foot">
          <button type="button" class="day-reports__print" :disabled="!report" @click="printReport">
            Print
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/plugins/axios';

const todayValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export default {
  name: 'KotDayReports',
  data() {
    return {
      date: todayValue(),
      kind: 'x',
      visible: false,
      report: null,
      loading: false,
      error: '',
    };
  },
  computed: {
    kindTitle() {
      return this.kind === 'z' ? 'Z report' : 'X report';
    },
    kindNote() {
      if (this.kind === 'z') {
        return 'Z report is the end-of-day copy. Printing does not clear the totals.';
      }
      return 'X report is the interim copy. You can print it again during the day.';
    },
  },
  methods: {
    open(kind) {
      this.kind = kind;
      this.visible = true;
      this.load();
    },
    close() {
      this.visible = false;
    },
    money(value) {
      return Number(value || 0).toLocaleString('en-LK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios.get(`hms/dash-board/day-report?date=${this.date}`);
        this.report = res.data || null;
      } catch (err) {
        this.report = null;
        const data = err && err.response && err.response.data;
        this.error = (data && data.message) || 'Could not load the report.';
      } finally {
        this.loading = false;
      }
    },
    printReport() {
      if (!this.report) return;
      const html = this.printHtml();
      const popup = window.open('', 'kotDayReport', 'popup=yes,width=860,height=720,scrollbars=yes,resizable=yes');
      if (!popup) return;
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      popup.focus();
      setTimeout(() => popup.print(), 300);
    },
    escape(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    printHtml() {
      const report = this.report;
      const title = this.kind === 'z' ? 'Z Report' : 'X Report';
      const subtitle = this.kind === 'z'
        ? 'End of day'
        : 'Interim';
      const org = report.organization || {};
      const rows = (list, cells) =>
        list.length
          ? list.map((row) => `<tr>${cells(row).map((cell) => `<td>${this.escape(cell)}</td>`).join('')}</tr>`).join('')
          : '<tr><td colspan="6">None</td></tr>';
      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title} ${this.escape(report.business_date_label)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
    h1 { margin: 0; font-size: 22px; }
    h2 { margin: 18px 0 6px; font-size: 14px; border-bottom: 1px solid #ccc; }
    p { margin: 2px 0; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 12px; }
    th, td { border-bottom: 1px solid #ddd; text-align: left; padding: 4px 6px; }
    th { background: #f3f4f6; }
    .meta { margin-top: 6px; color: #333; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  </style>
</head>
<body>
  <h1>${this.escape(org.name || 'Hotel')}</h1>
  <p>${this.escape(org.tagline || '')} ${this.escape(org.address || '')}</p>
  <p>${this.escape(org.phone || '')} ${this.escape(org.email || '')}</p>
  <h2>${title} — ${subtitle}</h2>
  <p class="meta">Business date ${this.escape(report.business_date_label)}</p>
  <p class="meta">Printed ${this.escape(new Date().toLocaleString())}</p>
  <div class="grid">
    <div>
      <h2>Sales</h2>
      <p>Orders ${report.orders.count} · Open ${report.orders.open_count} · Paid ${report.orders.paid_count}</p>
      <p>Partial ${report.orders.partial_count} · Cancelled ${report.orders.cancelled_count}</p>
      <p>Gross ${this.money(report.orders.gross)}</p>
      <p>Discount ${this.money(report.orders.discount)}</p>
      <p>Service charge ${this.money(report.orders.service_charge)}</p>
      <p>Tax ${this.money(report.orders.tax)}</p>
      <p><strong>Net ${this.money(report.orders.net)}</strong></p>
      <p>Collected ${this.money(report.orders.paid)}</p>
      <p>Due ${this.money(report.orders.due)}</p>
    </div>
    <div>
      <h2>Bookings and expenses</h2>
      <p>Bookings ${report.reservations.count}</p>
      <p>Booking amount ${this.money(report.reservations.amount)}</p>
      <p>Booking paid ${this.money(report.reservations.paid)}</p>
      <p>Booking due ${this.money(report.reservations.due)}</p>
      <p>Expenses ${report.expenses.count}</p>
      <p>Expense amount ${this.money(report.expenses.amount)}</p>
      <p>Completed payments ${this.money(report.payments.total_completed)}</p>
      <p>Pending payments ${this.money(report.payments.total_pending)}</p>
    </div>
  </div>
  <h2>Sales by type</h2>
  <table>
    <thead><tr><th>Type</th><th>Orders</th><th>Net</th><th>Collected</th></tr></thead>
    <tbody>${rows(report.orders.by_type, (row) => [row.name, row.count, this.money(row.net), this.money(row.paid)])}</tbody>
  </table>
  <h2>Sales by status</h2>
  <table>
    <thead><tr><th>Status</th><th>Orders</th><th>Net</th><th>Collected</th></tr></thead>
    <tbody>${rows(report.orders.by_status, (row) => [row.name, row.count, this.money(row.net), this.money(row.paid)])}</tbody>
  </table>
  <h2>Waiters</h2>
  <table>
    <thead><tr><th>Waiter</th><th>Orders</th><th>Net</th><th>Collected</th></tr></thead>
    <tbody>${rows(report.orders.by_waiter, (row) => [row.name, row.count, this.money(row.net), this.money(row.paid)])}</tbody>
  </table>
  <h2>Payments</h2>
  <table>
    <thead><tr><th>Type</th><th>Status</th><th>Count</th><th>Amount</th></tr></thead>
    <tbody>${rows(report.payments.rows, (row) => [row.type, row.status, row.count, this.money(row.amount)])}</tbody>
  </table>
  <h2>All orders</h2>
  <table>
    <thead><tr><th>Order</th><th>Type</th><th>Place</th><th>Waiter</th><th>Status</th><th>Net</th><th>Paid</th><th>Due</th></tr></thead>
    <tbody>${rows(report.lines, (row) => [row.order_number, row.type, row.place, row.waiter, row.status_label, this.money(row.net), this.money(row.paid), this.money(row.due)])}</tbody>
  </table>
  <h2>Bookings</h2>
  <table>
    <thead><tr><th>Reference</th><th>Status</th><th>Amount</th><th>Paid</th><th>Due</th></tr></thead>
    <tbody>${rows(report.reservations.rows, (row) => [row.reference_number, row.status_label, this.money(row.amount), this.money(row.paid), this.money(row.due)])}</tbody>
  </table>
  <h2>Expenses</h2>
  <table>
    <thead><tr><th>Reference</th><th>For</th><th>Remark</th><th>Amount</th></tr></thead>
    <tbody>${rows(report.expenses.rows, (row) => [row.reference_number, row.expense_for, row.remark, this.money(row.amount)])}</tbody>
  </table>
</body>
</html>`;
    },
  },
};
</script>

<style scoped>
.day-reports-root {
  width: 100%;
  min-width: 0;
}

.day-reports-dock {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
  height: 100%;
}

.day-reports-dock__btn {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: #058397;
  color: #ffffff !important;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18);
}

.day-reports-modal {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  box-sizing: border-box;
}

.day-reports {
  width: min(720px, 100%);
  max-height: min(86vh, 860px);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  color: #0f172a;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.28);
  box-sizing: border-box;
  overflow: hidden;
}

.day-reports__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 10px 6px;
}

.day-reports__head h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.day-reports__close {
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a !important;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.day-reports__date {
  width: 148px;
  max-width: 100%;
  box-sizing: border-box;
  height: 28px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0 6px;
  font-size: 12px;
  color: #0f172a !important;
  background: #ffffff;
}

.day-reports__tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 0 10px 6px;
}

.day-reports__tab,
.day-reports__print,
.day-reports__retry {
  height: 30px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #0f172a !important;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.day-reports__tab--active {
  background: #058397;
  border-color: #058397;
  color: #ffffff !important;
}

.day-reports__note {
  margin: 0 10px 8px;
  font-size: 11px;
  line-height: 1.35;
  color: #475569;
}

.day-reports__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 0 10px 8px;
}

.day-reports__state {
  flex: 1;
  padding: 16px 10px;
  font-size: 12px;
  color: #475569;
}

.day-reports__state--error {
  color: #b91c1c;
}

.day-reports__retry {
  display: block;
  margin-top: 8px;
}

.day-reports__block {
  margin-bottom: 12px;
}

.day-reports__block h3 {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
}

.day-reports__empty {
  margin: 0;
  font-size: 11px;
  color: #64748b;
}

.day-reports dl {
  margin: 0;
}

.day-reports dl div {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 11px;
}

.day-reports dt {
  color: #475569;
}

.day-reports dd {
  margin: 0;
  font-weight: 600;
  color: #0f172a;
}

.day-reports .is-strong dd,
.day-reports .is-strong dt {
  color: #0f172a;
  font-weight: 700;
}

.day-reports .is-due dd {
  color: #b91c1c;
}

.day-reports table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.day-reports th,
.day-reports td {
  text-align: left;
  padding: 3px 2px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
}

.day-reports th {
  color: #64748b;
  font-weight: 700;
}

.day-reports__foot {
  flex-shrink: 0;
  padding: 8px 10px 10px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.day-reports__print {
  width: 100%;
  background: #058397;
  border-color: #058397;
  color: #ffffff !important;
}

.day-reports__print:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>

<style>
html.kot-dark .day-reports-dock__btn {
  background: #058397 !important;
  color: #ffffff !important;
}

html.kot-dark .day-reports {
  background: #1e2c36;
  color: #ffffff;
}

html.kot-dark .day-reports__close {
  background: #2c3c48 !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

html.kot-dark .day-reports__head h2,
html.kot-dark .day-reports__block h3,
html.kot-dark .day-reports dd,
html.kot-dark .day-reports th,
html.kot-dark .day-reports td,
html.kot-dark .day-reports .is-strong dt,
html.kot-dark .day-reports .is-strong dd {
  color: #ffffff !important;
}

html.kot-dark .day-reports__note,
html.kot-dark .day-reports dt,
html.kot-dark .day-reports__empty,
html.kot-dark .day-reports__state,
html.kot-dark .day-reports th {
  color: #d5dee6 !important;
}

html.kot-dark .day-reports__date,
html.kot-dark .day-reports__tab,
html.kot-dark .day-reports__retry {
  background: #2c3c48 !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

html.kot-dark .day-reports__tab--active,
html.kot-dark .day-reports__print {
  background: #058397 !important;
  color: #ffffff !important;
  border-color: #058397 !important;
}

html.kot-dark .day-reports dl div,
html.kot-dark .day-reports th,
html.kot-dark .day-reports td,
html.kot-dark .day-reports__foot {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background: transparent;
}

html.kot-dark .day-reports__foot {
  background: #1e2c36 !important;
}

html.kot-dark .day-reports .is-due dd {
  color: #fda4af !important;
}
</style>
